import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, DollarSign, Building2, X, Users, Calendar, Star, Scissors } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';

const RealEstateManagingPartnerReports = () => {
  const [searchParams] = useSearchParams();
  const template = searchParams.get('template');
  const industry = searchParams.get('industry');
  const role = searchParams.get('role');

  const [selectedModal, setSelectedModal] = useState(null);
  const [modalData, setModalData] = useState(null);

  const openModal = (type, data) => {
    if (typeof type === 'string' && !data) {
      console.log('Opening modal:', type);
    } else {
      setSelectedModal(type);
      setModalData(data);
    }
  };

  const closeModal = () => {
    setSelectedModal(null);
    setModalData(null);
  };

  // Show Hair Salon - Barber version
  if ((template === 'Hair Salon' || industry === 'Hair Salon') && role === 'Barber') {
    // Monthly performance data
    const monthlyPerformanceData = [
      { month: 'Jan', revenue: 28.5, customers: 385, satisfaction: 92 },
      { month: 'Feb', revenue: 30.2, customers: 412, satisfaction: 93 },
      { month: 'Mar', revenue: 32.8, customers: 445, satisfaction: 94 },
      { month: 'Apr', revenue: 35.5, customers: 478, satisfaction: 95 },
      { month: 'May', revenue: 38.2, customers: 512, satisfaction: 96 },
      { month: 'Jun', revenue: 40.5, customers: 545, satisfaction: 97 },
      { month: 'Jul', revenue: 42.8, customers: 578, satisfaction: 96 },
      { month: 'Aug', revenue: 41.2, customers: 562, satisfaction: 97 },
      { month: 'Sep', revenue: 39.8, customers: 538, satisfaction: 98 },
      { month: 'Oct', revenue: 37.5, customers: 502, satisfaction: 97 },
      { month: 'Nov', revenue: 36.2, customers: 489, satisfaction: 98 },
      { month: 'Dec', revenue: 45.8, customers: 625, satisfaction: 98 }
    ];

    // Service performance data
    const servicePerformanceData = [
      { 
        service: 'Hair Styling', 
        revenue: 189.5, 
        appointments: 2485, 
        avgPrice: 76.3, 
        growth: '+15.2%',
        topStylist: 'Sarah Williams'
      },
      { 
        service: 'Color Services', 
        revenue: 168.2, 
        appointments: 1892, 
        avgPrice: 88.9, 
        growth: '+18.5%',
        topStylist: 'Michael Chen'
      },
      { 
        service: 'Nail Care', 
        revenue: 82.5, 
        appointments: 1654, 
        avgPrice: 49.9, 
        growth: '+12.3%',
        topStylist: 'Jennifer Lopez'
      },
      { 
        service: 'Other Services', 
        revenue: 28.8, 
        appointments: 642, 
        avgPrice: 44.9, 
        growth: '+8.7%',
        topStylist: 'Emma Johnson'
      }
    ];

    // Employee performance data
    const employeePerformanceData = [
      { name: 'Sarah Williams', department: 'Hair Styling', revenue: 62.5, appointments: 248, rating: 4.9, efficiency: 98 },
      { name: 'Michael Chen', department: 'Color Services', revenue: 58.2, appointments: 215, rating: 4.8, efficiency: 95 },
      { name: 'Emma Johnson', department: 'Reception', revenue: 45.8, appointments: 198, rating: 4.9, efficiency: 97 },
      { name: 'Emily Rodriguez', department: 'Hair Styling', revenue: 48.5, appointments: 192, rating: 4.7, efficiency: 94 },
      { name: 'Jennifer Lopez', department: 'Nail Care', revenue: 38.2, appointments: 175, rating: 4.8, efficiency: 96 },
      { name: 'David Martinez', department: 'Color Services', revenue: 35.8, appointments: 168, rating: 4.6, efficiency: 92 },
      { name: 'Maria Garcia', department: 'Nail Care', revenue: 32.5, appointments: 158, rating: 4.7, efficiency: 93 },
      { name: 'Robert Taylor', department: 'Color Services', revenue: 28.9, appointments: 142, rating: 4.5, efficiency: 90 }
    ];

    // Customer demographics
    const customerDemographics = [
      { segment: 'Regular Clients', count: 485, percentage: 54, revenue: 248.5 },
      { segment: 'New Clients', count: 178, percentage: 20, revenue: 82.3 },
      { segment: 'VIP Members', count: 89, percentage: 10, revenue: 95.8 },
      { segment: 'Walk-ins', count: 143, percentage: 16, revenue: 42.4 }
    ];

    // Monthly trends
    const trendData = [
      { metric: 'Average Wait Time', current: '8 min', previous: '12 min', change: '-33%', status: 'improved' },
      { metric: 'Booking Rate', current: '94%', previous: '89%', change: '+5.6%', status: 'improved' },
      { metric: 'No-Show Rate', current: '3.2%', previous: '5.8%', change: '-44.8%', status: 'improved' },
      { metric: 'Customer Retention', current: '87%', previous: '82%', change: '+6.1%', status: 'improved' },
      { metric: 'Average Transaction', current: '$85', previous: '$78', change: '+9.0%', status: 'improved' },
      { metric: 'Products per Visit', current: '1.8', previous: '1.4', change: '+28.6%', status: 'improved' }
    ];

    return (
      <div className="min-h-screen bg-black text-white p-6 overflow-y-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Salon Reports</h1>
          <p className="text-gray-400">Comprehensive business insights and performance analytics</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-black border border-white rounded-xl p-6 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Annual Revenue</p>
                <h2 className="text-3xl font-bold">$469K</h2>
              </div>
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign size={24} className="text-green-500" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
                <TrendingUp size={16} />
                +14.8%
              </span>
              <span className="text-gray-400 text-sm">vs last year</span>
            </div>
          </div>

          <div className="bg-black border border-white rounded-xl p-6 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Total Appointments</p>
                <h2 className="text-3xl font-bold">7,671</h2>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Calendar size={24} className="text-purple-500" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
                <TrendingUp size={16} />
                +12.3%
              </span>
              <span className="text-gray-400 text-sm">year over year</span>
            </div>
          </div>

          <div className="bg-black border border-white rounded-xl p-6 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Avg Satisfaction</p>
                <h2 className="text-3xl font-bold">96%</h2>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Star size={24} className="text-blue-500" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
                <TrendingUp size={16} />
                +4.2%
              </span>
              <span className="text-gray-400 text-sm">customer rating</span>
            </div>
          </div>

          <div className="bg-black border border-white rounded-xl p-6 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Active Clients</p>
                <h2 className="text-3xl font-bold">895</h2>
              </div>
              <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center">
                <Users size={24} className="text-pink-500" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-sm font-semibold flex items-center gap-1">
                <TrendingUp size={16} />
                +16.8%
              </span>
              <span className="text-gray-400 text-sm">client base growth</span>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-black border border-white rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4">Monthly Performance Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis yAxisId="left" stroke="#9CA3AF" />
                <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#8B5CF6" strokeWidth={2} name="Revenue ($K)" />
                <Line yAxisId="right" type="monotone" dataKey="customers" stroke="#10B981" strokeWidth={2} name="Customers" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-black border border-white rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4">Customer Satisfaction Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" domain={[90, 100]} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="satisfaction" fill="#3B82F6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Service Performance Table */}
        <div className="bg-black border border-white rounded-xl p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Service Performance Analysis</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Service</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Annual Revenue</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Appointments</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Avg Price</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Growth</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Top Performer</th>
                </tr>
              </thead>
              <tbody>
                {servicePerformanceData.map((service, idx) => (
                  <tr key={idx} className="border-b border-gray-800 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-4 font-semibold">{service.service}</td>
                    <td className="py-4 px-4 text-right text-green-400 font-semibold">${service.revenue}K</td>
                    <td className="py-4 px-4 text-right font-semibold">{service.appointments}</td>
                    <td className="py-4 px-4 text-right">${service.avgPrice}</td>
                    <td className="py-4 px-4 text-right">
                      <span className="text-green-400 font-semibold">{service.growth}</span>
                    </td>
                    <td className="py-4 px-4">{service.topStylist}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Employee Performance Table */}
        <div className="bg-black border border-white rounded-xl p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Employee Performance Report</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Employee</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Department</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Revenue</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Appointments</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Rating</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Efficiency</th>
                </tr>
              </thead>
              <tbody>
                {employeePerformanceData.map((employee, idx) => (
                  <tr key={idx} className="border-b border-gray-800 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-4 font-semibold">{employee.name}</td>
                    <td className="py-4 px-4 text-gray-400">{employee.department}</td>
                    <td className="py-4 px-4 text-right text-green-400 font-semibold">${employee.revenue}K</td>
                    <td className="py-4 px-4 text-right font-semibold">{employee.appointments}</td>
                    <td className="py-4 px-4 text-right">
                      <span className="text-yellow-400 font-semibold">⭐ {employee.rating}</span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="text-blue-400 font-semibold">{employee.efficiency}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Key Metrics & Customer Demographics */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-black border border-white rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4">Key Performance Metrics</h3>
            <div className="space-y-4">
              {trendData.map((metric, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                  <div>
                    <p className="font-semibold">{metric.metric}</p>
                    <p className="text-2xl font-bold text-purple-400 mt-1">{metric.current}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">vs previous</p>
                    <p className="text-green-400 font-semibold flex items-center gap-1 justify-end mt-1">
                      <TrendingUp size={16} />
                      {metric.change}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-black border border-white rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4">Customer Demographics</h3>
            <div className="space-y-4">
              {customerDemographics.map((segment, idx) => (
                <div key={idx}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">{segment.segment}</span>
                    <span className="text-gray-400">{segment.count} clients</span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-3 mb-1">
                    <div
                      className="bg-purple-500 h-3 rounded-full transition-all"
                      style={{ width: `${segment.percentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{segment.percentage}% of total</span>
                    <span className="text-green-400 font-semibold">${segment.revenue}K revenue</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default Real Estate Reports below
  const riskTrendData = [
    { month: 'Jan', financial: 45, operational: 30, market: 25, regulatory: 20 },
    { month: 'Feb', financial: 50, operational: 35, market: 30, regulatory: 22 },
    { month: 'Mar', financial: 48, operational: 40, market: 28, regulatory: 25 },
    { month: 'Apr', financial: 52, operational: 38, market: 35, regulatory: 28 },
    { month: 'May', financial: 55, operational: 42, market: 32, regulatory: 30 },
    { month: 'Jun', financial: 58, operational: 45, market: 38, regulatory: 32 },
    { month: 'Jul', financial: 60, operational: 48, market: 40, regulatory: 35 },
    { month: 'Aug', financial: 55, operational: 46, market: 38, regulatory: 33 },
    { month: 'Sep', financial: 52, operational: 44, market: 35, regulatory: 31 },
    { month: 'Oct', financial: 48, operational: 40, market: 32, regulatory: 28 },
    { month: 'Nov', financial: 45, operational: 38, market: 30, regulatory: 26 },
    { month: 'Dec', financial: 42, operational: 35, market: 28, regulatory: 24 }
  ];

  // Monthly cash flow data
  const cashFlowData = [
    { month: 'Jan', cashFlow: 6.2 },
    { month: 'Feb', cashFlow: 5.8 },
    { month: 'Mar', cashFlow: 6.5 },
    { month: 'Apr', cashFlow: 7.2 },
    { month: 'May', cashFlow: 6.9 },
    { month: 'Jun', cashFlow: 7.5 },
    { month: 'Jul', cashFlow: 7.8 },
    { month: 'Aug', cashFlow: 7.3 },
    { month: 'Sep', cashFlow: 7.9 },
    { month: 'Oct', cashFlow: 8.2 },
    { month: 'Nov', cashFlow: 7.6 },
    { month: 'Dec', cashFlow: 8.5 }
  ];

  // Risk & Contingency Analysis
  const riskContingency = [
    { project: 'Uptown Dallas Tower', risk: 'High', probability: 'Medium', contingency: '$2.5M' },
    { project: 'Frisco Tech Campus', risk: 'Medium', probability: 'Low', contingency: '$1.8M' },
    { project: 'Downtown Office Complex', risk: 'Low', probability: 'Low', contingency: '$0.9M' },
    { project: 'Legacy West Office Park', risk: 'High', probability: 'High', contingency: '$3.2M' }
  ];

  // Profitability Analysis - Extended data
  const profitabilityData = [
    { property: 'Uptown Dallas Tower', revenue: 47.5, expenses: 32.8, profit: 14.7, roi: '31%', occupancy: '98%', units: 250, avgRent: 3200 },
    { property: 'Frisco Tech Campus', revenue: 95.0, expenses: 68.5, profit: 26.5, roi: '28%', occupancy: '97%', units: 450, avgRent: 2800 },
    { property: 'Victory Park Complex', revenue: 88.0, expenses: 65.2, profit: 22.8, roi: '26%', occupancy: '93%', units: 380, avgRent: 3100 },
    { property: 'Knox Henderson Retail', revenue: 18.5, expenses: 14.2, profit: 4.3, roi: '23%', occupancy: '92%', units: 85, avgRent: 2900 },
    { property: 'Design District Warehouse', revenue: 28.5, expenses: 22.8, profit: 5.7, roi: '20%', occupancy: '90%', units: 120, avgRent: 2400 },
    { property: 'Legacy West Office Park', revenue: 72.0, expenses: 52.4, profit: 19.6, roi: '27%', occupancy: '95%', units: 340, avgRent: 2850 },
    { property: 'Downtown Office Complex', revenue: 65.0, expenses: 48.2, profit: 16.8, roi: '26%', occupancy: '96%', units: 290, avgRent: 3000 },
    { property: 'Arts District Cultural', revenue: 42.0, expenses: 32.5, profit: 9.5, roi: '23%', occupancy: '89%', units: 180, avgRent: 2650 },
    { property: 'Deep Ellum Lofts', revenue: 35.2, expenses: 27.8, profit: 7.4, roi: '21%', occupancy: '94%', units: 160, avgRent: 2750 },
    { property: 'Bishop Arts Retail Center', revenue: 22.8, expenses: 18.4, profit: 4.4, roi: '19%', occupancy: '91%', units: 95, avgRent: 2500 },
    { property: 'Addison Conference Center', revenue: 31.5, expenses: 25.2, profit: 6.3, roi: '20%', occupancy: '88%', units: 140, avgRent: 2600 },
    { property: 'Richardson Tech Plaza', revenue: 54.3, expenses: 41.8, profit: 12.5, roi: '23%', occupancy: '93%', units: 260, avgRent: 2900 },
    { property: 'McKinney Avenue Shops', revenue: 19.7, expenses: 16.3, profit: 3.4, roi: '17%', occupancy: '87%', units: 80, avgRent: 2400 },
    { property: 'Trinity Groves Mixed Use', revenue: 46.9, expenses: 36.2, profit: 10.7, roi: '23%', occupancy: '92%', units: 210, avgRent: 2800 },
    { property: 'Lakewood Shopping Plaza', revenue: 27.4, expenses: 21.8, profit: 5.6, roi: '20%', occupancy: '94%', units: 115, avgRent: 2700 }
  ];

  // Detailed Risk Factors
  const detailedRisks = [
    { category: 'Market Volatility', impact: 'High', probability: 'Medium', mitigation: 'Diversification Strategy' },
    { category: 'Interest Rate Changes', impact: 'High', probability: 'High', mitigation: 'Fixed Rate Locks' },
    { category: 'Tenant Default', impact: 'Medium', probability: 'Low', mitigation: 'Credit Screening' },
    { category: 'Property Maintenance', impact: 'Medium', probability: 'Medium', mitigation: 'Preventive Programs' },
    { category: 'Regulatory Changes', impact: 'Low', probability: 'Low', mitigation: 'Compliance Monitoring' }
  ];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'High': return 'text-red-500 bg-red-500/20';
      case 'Medium': return 'text-yellow-500 bg-yellow-500/20';
      case 'Low': return 'text-green-500 bg-green-500/20';
      default: return 'text-gray-500 bg-gray-500/20';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6 overflow-y-auto">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">Reports & Analytics</h1>
        <p className="text-gray-400 text-xs md:text-base">Commercial Real Estate Portfolio Performance & Risk Analysis</p>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
        {/* Total Budget Allocated */}
        <div 
          onClick={() => openModal('kpi', {
            title: 'Total Budget Allocated',
            value: '$717.5M',
            change: '+8.5%',
            details: 'Portfolio budget allocation across 15 commercial properties. Includes capital expenditures, operational costs, and contingency reserves. Budget increased by $56M compared to last quarter due to expansion initiatives.',
            breakdown: [
              { category: 'Capital Expenditures', amount: '$285M', percentage: '40%' },
              { category: 'Operational Costs', amount: '$320M', percentage: '45%' },
              { category: 'Contingency Reserve', amount: '$72.5M', percentage: '10%' },
              { category: 'Marketing & Leasing', amount: '$40M', percentage: '5%' }
            ]
          })}
          className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 cursor-pointer hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div>
              <p className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Total Budget Allocated</p>
              <h2 className="text-2xl md:text-3xl font-bold">$717.5M</h2>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-green-500 md:hidden" />
              <DollarSign size={24} className="text-green-500 hidden md:block" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-500 text-xs md:text-sm font-semibold flex items-center gap-1">
              <TrendingUp size={14} className="md:hidden" />
              <TrendingUp size={16} className="hidden md:block" />
              +8.5%
            </span>
            <span className="text-gray-400 text-[10px] md:text-sm">vs last quarter</span>
          </div>
        </div>

        {/* Total Revenue Expected */}
        <div 
          onClick={() => openModal('kpi', {
            title: 'Total Revenue Expected',
            value: '$425.5M',
            change: '+12.3%',
            details: 'Projected annual revenue from all properties including rent, parking, amenities, and additional services. Strong growth driven by high occupancy rates and rent increases.',
            breakdown: [
              { category: 'Base Rent', amount: '$340M', percentage: '80%' },
              { category: 'Parking Revenue', amount: '$42.5M', percentage: '10%' },
              { category: 'Amenity Fees', amount: '$25.5M', percentage: '6%' },
              { category: 'Other Services', amount: '$17.5M', percentage: '4%' }
            ]
          })}
          className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 cursor-pointer hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div>
              <p className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Total Revenue Expected</p>
              <h2 className="text-2xl md:text-3xl font-bold">$425.5M</h2>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp size={20} className="text-teal-500 md:hidden" />
              <TrendingUp size={24} className="text-teal-500 hidden md:block" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-500 text-xs md:text-sm font-semibold flex items-center gap-1">
              <TrendingUp size={14} className="md:hidden" />
              <TrendingUp size={16} className="hidden md:block" />
              +12.3%
            </span>
            <span className="text-gray-400 text-[10px] md:text-sm">Annual growth</span>
          </div>
        </div>

        {/* Total Expenses Incurred */}
        <div 
          onClick={() => openModal('kpi', {
            title: 'Total Expenses Incurred',
            value: '$318.7M',
            change: '75% of Budget',
            details: 'Year-to-date expenses across operations, maintenance, utilities, staffing, and capital improvements. Tracking 25% below budget with strong cost management.',
            breakdown: [
              { category: 'Property Management', amount: '$95.6M', percentage: '30%' },
              { category: 'Maintenance & Repairs', amount: '$79.7M', percentage: '25%' },
              { category: 'Utilities', amount: '$63.7M', percentage: '20%' },
              { category: 'Staffing', amount: '$47.8M', percentage: '15%' },
              { category: 'Insurance & Taxes', amount: '$31.9M', percentage: '10%' }
            ]
          })}
          className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 cursor-pointer hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div>
              <p className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Total Expenses Incurred</p>
              <h2 className="text-2xl md:text-3xl font-bold">$318.7M</h2>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Building2 size={20} className="text-purple-500 md:hidden" />
              <Building2 size={24} className="text-purple-500 hidden md:block" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-[10px] md:text-sm">75% of Budget</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
        {/* Risk Trend Analysis - Takes 2 columns on desktop */}
        <div className="lg:col-span-2 bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
          <div className="flex items-start md:items-center justify-between mb-4 md:mb-6">
            <div>
              <h3 className="text-lg md:text-xl font-bold mb-0.5 md:mb-1">Risk Trend Analysis</h3>
              <p className="text-gray-400 text-[10px] md:text-sm">Monthly risk assessment across categories</p>
            </div>
          </div>
          
          <ResponsiveContainer width="100%" height={220} className="md:hidden">
            <LineChart data={riskTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="month" stroke="#666" tick={{ fontSize: 8 }} interval={2} />
              <YAxis stroke="#666" tick={{ fontSize: 8 }} width={25} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px', fontSize: '10px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Line type="monotone" dataKey="financial" stroke="#8B5CF6" strokeWidth={2} name="Financial" dot={false} />
              <Line type="monotone" dataKey="operational" stroke="#EF4444" strokeWidth={2} name="Operational" dot={false} />
              <Line type="monotone" dataKey="market" stroke="#F59E0B" strokeWidth={2} name="Market" dot={false} />
              <Line type="monotone" dataKey="regulatory" stroke="#10B981" strokeWidth={2} name="Regulatory" dot={false} />
            </LineChart>
          </ResponsiveContainer>

          <ResponsiveContainer width="100%" height={380} className="hidden md:block">
            <LineChart data={riskTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="month" stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend />
              <Line type="monotone" dataKey="financial" stroke="#8B5CF6" strokeWidth={2} name="Financial Risk" />
              <Line type="monotone" dataKey="operational" stroke="#EF4444" strokeWidth={2} name="Operational Risk" />
              <Line type="monotone" dataKey="market" stroke="#F59E0B" strokeWidth={2} name="Market Risk" />
              <Line type="monotone" dataKey="regulatory" stroke="#10B981" strokeWidth={2} name="Regulatory Risk" />
            </LineChart>
          </ResponsiveContainer>

          {/* Mobile Legend */}
          <div className="md:hidden flex flex-wrap gap-2 mt-2 justify-center">
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-purple-500 rounded-full"></div><span className="text-[10px] text-gray-400">Financial</span></div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-red-500 rounded-full"></div><span className="text-[10px] text-gray-400">Operational</span></div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-yellow-500 rounded-full"></div><span className="text-[10px] text-gray-400">Market</span></div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-green-500 rounded-full"></div><span className="text-[10px] text-gray-400">Regulatory</span></div>
          </div>
        </div>

        {/* Right Column - Smaller Cards */}
        <div className="space-y-4 md:space-y-6">
          {/* Monthly Cash Flow Trends */}
          <div className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Monthly Cash Flow</h3>
            <ResponsiveContainer width="100%" height={100} className="md:hidden">
              <BarChart data={cashFlowData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#666" tick={{ fontSize: 8 }} interval={2} />
                <YAxis stroke="#666" tick={{ fontSize: 8 }} width={20} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px', fontSize: '10px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="cashFlow" fill="#10B981" name="Cash Flow ($M)" />
              </BarChart>
            </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={120} className="hidden md:block">
              <BarChart data={cashFlowData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#666" tick={{ fontSize: 10 }} />
                <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#000', border: '1px solid #fff', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="cashFlow" fill="#10B981" name="Cash Flow ($M)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Risk & Contingency Analysis */}
          <div className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Risk & Contingency</h3>
            <div className="space-y-2 md:space-y-3">
              {riskContingency.map((item, idx) => (
                <div 
                  key={idx} 
                  className="text-sm cursor-pointer hover:bg-white/5 p-2 rounded transition-colors"
                  onClick={() => openModal('risk', item)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white font-semibold text-[10px] md:text-xs truncate flex-1 pr-2">{item.project}</span>
                    <span className="text-gray-400 text-[10px] md:text-xs flex-shrink-0">{item.contingency}</span>
                  </div>
                  <div className="flex items-center gap-1 md:gap-2">
                    <span className={`px-1.5 md:px-2 py-0.5 rounded text-[10px] md:text-xs font-semibold ${getRiskColor(item.risk)}`}>
                      {item.risk}
                    </span>
                    <span className={`px-1.5 md:px-2 py-0.5 rounded text-[10px] md:text-xs font-semibold ${getRiskColor(item.probability)}`}>
                      {item.probability}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Second Row - Profitability & Risk Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
        {/* Profitability Analysis - Takes 2 columns on desktop */}
        <div className="lg:col-span-2 bg-black border border-gray-400 rounded-xl p-4 md:p-6 flex flex-col hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300" style={{ minHeight: '300px', maxHeight: '400px' }}>
          <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Profitability Analysis</h3>
          <div className="overflow-y-auto flex-1 pr-2 -mx-4 px-4 md:mx-0 md:px-0" style={{ scrollbarWidth: 'thin', scrollbarColor: '#4B5563 #1F2937' }}>
            {/* Desktop Table */}
            <table className="w-full hidden md:table">
              <thead className="sticky top-0 bg-black z-10">
                <tr className="border-b border-gray-400">
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Property</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Revenue ($M)</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Expenses ($M)</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Profit ($M)</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">ROI</th>
                  <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Occupancy</th>
                </tr>
              </thead>
              <tbody>
                {profitabilityData.map((property, idx) => (
                  <tr 
                    key={idx} 
                    className="border-b border-gray-800 hover:bg-white/5 cursor-pointer"
                    onClick={() => openModal('property', property)}
                  >
                    <td className="py-4 px-2 text-white font-semibold text-sm">{property.property}</td>
                    <td className="py-4 px-2 text-white text-sm">${property.revenue}</td>
                    <td className="py-4 px-2 text-white text-sm">${property.expenses}</td>
                    <td className="py-4 px-2 text-green-500 font-semibold text-sm">${property.profit}</td>
                    <td className="py-4 px-2 text-white text-sm">{property.roi}</td>
                    <td className="py-4 px-2 text-white text-sm">{property.occupancy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-2">
              {profitabilityData.slice(0, 8).map((property, idx) => (
                <div 
                  key={idx} 
                  className="bg-gray-900/50 rounded-lg p-3 border border-gray-700 cursor-pointer"
                  onClick={() => openModal('property', property)}
                >
                  <p className="text-xs font-semibold text-white truncate mb-1">{property.property}</p>
                  <div className="grid grid-cols-3 gap-2 text-[10px]">
                    <div>
                      <span className="text-gray-400">Revenue</span>
                      <p className="text-white">${property.revenue}M</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Profit</span>
                      <p className="text-green-500 font-semibold">${property.profit}M</p>
                    </div>
                    <div>
                      <span className="text-gray-400">ROI</span>
                      <p className="text-teal-500 font-semibold">{property.roi}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Detailed Risk Factors */}
        <div className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 flex flex-col hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300" style={{ minHeight: '300px', maxHeight: '400px' }}>
          <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Detailed Risk Factors</h3>
          <div className="space-y-2 md:space-y-3 overflow-y-auto flex-1 pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#4B5563 #1F2937' }}>
            {detailedRisks.map((risk, idx) => (
              <div 
                key={idx} 
                className="p-2 md:p-3 bg-white/5 border border-gray-400/20 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => openModal('detailedRisk', risk)}
              >
                <div className="text-white font-semibold text-xs md:text-sm mb-1 md:mb-2">{risk.category}</div>
                <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2 flex-wrap">
                  <span className={`px-1.5 md:px-2 py-0.5 rounded text-[10px] md:text-xs font-semibold ${getRiskColor(risk.impact)}`}>
                    Impact: {risk.impact}
                  </span>
                  <span className={`px-1.5 md:px-2 py-0.5 rounded text-[10px] md:text-xs font-semibold ${getRiskColor(risk.probability)}`}>
                    Prob: {risk.probability}
                  </span>
                </div>
                <div className="text-[10px] md:text-xs text-gray-400">{risk.mitigation}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommended Actions Section */}
      <div className="bg-gradient-to-r from-purple-900/50 to-purple-800/50 border border-purple-500/50 rounded-xl p-4 md:p-6">
        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
          <AlertTriangle className="text-purple-400" size={20} />
          <h2 className="text-lg md:text-2xl font-bold">Recommended Actions</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {/* Action 1 */}
          <div 
            className="bg-black/50 border border-gray-400/30 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-black/70 transition-colors"
            onClick={() => openModal('action', {
              title: 'Review Legacy West Budget',
              description: 'High risk with high probability detected. Contingency fund of $3.2M allocated.',
              priority: 'URGENT',
              details: 'Legacy West Office Park is showing concerning trends with construction delays, material cost overruns, and potential tenant lease renegotiations. Immediate budget review required.',
              recommendations: [
                'Conduct detailed cost analysis by January 15',
                'Meet with general contractor to address delays',
                'Review tenant agreements for flexibility',
                'Consider accessing contingency fund if needed'
              ]
            })}
          >
            <div className="flex items-start gap-2 md:gap-3">
              <div className="w-7 h-7 md:w-8 md:h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-red-500" size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-white font-semibold text-xs md:text-sm mb-0.5 md:mb-1 truncate">Review Legacy West Budget</h4>
                <p className="text-gray-400 text-[10px] md:text-xs mb-1 md:mb-2 line-clamp-2">High risk with high probability detected. Contingency fund of $3.2M allocated.</p>
                <span className="text-red-400 text-[10px] md:text-xs font-semibold">URGENT</span>
              </div>
            </div>
          </div>

          {/* Action 2 */}
          <div 
            className="bg-black/50 border border-gray-400/30 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-black/70 transition-colors"
            onClick={() => openModal('action', {
              title: 'Optimize Cash Flow Strategy',
              description: 'Q4 showing 18% increase. Consider reinvestment opportunities.',
              priority: 'RECOMMENDED',
              details: 'Strong Q4 cash flow performance presents opportunity for strategic reinvestment in portfolio expansion or property improvements.',
              recommendations: [
                'Evaluate acquisition opportunities in Dallas market',
                'Consider property enhancement projects',
                'Review debt refinancing options',
                'Increase reserve fund allocation'
              ]
            })}
          >
            <div className="flex items-start gap-2 md:gap-3">
              <div className="w-7 h-7 md:w-8 md:h-8 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="text-yellow-500" size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-white font-semibold text-xs md:text-sm mb-0.5 md:mb-1 truncate">Optimize Cash Flow Strategy</h4>
                <p className="text-gray-400 text-[10px] md:text-xs mb-1 md:mb-2 line-clamp-2">Q4 showing 18% increase. Consider reinvestment opportunities.</p>
                <span className="text-yellow-400 text-[10px] md:text-xs font-semibold">RECOMMENDED</span>
              </div>
            </div>
          </div>

          {/* Action 3 */}
          <div 
            className="bg-black/50 border border-gray-400/30 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-black/70 transition-colors"
            onClick={() => openModal('action', {
              title: 'Capitalize on High Occupancy',
              description: 'Uptown Tower at 98% occupancy. Potential for rate increase.',
              priority: 'OPPORTUNITY',
              details: 'Uptown Dallas Tower maintaining 98% occupancy with strong market demand. Market analysis supports 5-8% rent increase for new leases.',
              recommendations: [
                'Implement 6% rent increase for new leases',
                'Offer renewal incentives to existing tenants',
                'Upgrade amenities to justify premium pricing',
                'Market property to high-value corporate tenants'
              ]
            })}
          >
            <div className="flex items-start gap-2 md:gap-3">
              <div className="w-7 h-7 md:w-8 md:h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-green-500" size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-white font-semibold text-xs md:text-sm mb-0.5 md:mb-1 truncate">Capitalize on High Occupancy</h4>
                <p className="text-gray-400 text-[10px] md:text-xs mb-1 md:mb-2 line-clamp-2">Uptown Tower at 98% occupancy. Potential for rate increase.</p>
                <span className="text-green-400 text-[10px] md:text-xs font-semibold">OPPORTUNITY</span>
              </div>
            </div>
          </div>

          {/* Action 4 */}
          <div 
            className="bg-black/50 border border-gray-400/30 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-black/70 transition-colors"
            onClick={() => openModal('action', {
              title: 'Review Interest Rate Exposure',
              description: 'High probability risk identified. Consider fixed-rate locks.',
              priority: 'PRIORITY',
              details: 'Portfolio has significant exposure to variable interest rates. Fed policy changes could impact profitability by $15-20M annually.',
              recommendations: [
                'Refinance variable rate loans to fixed',
                'Lock in current favorable rates',
                'Negotiate rate caps with lenders',
                'Develop interest rate hedging strategy'
              ]
            })}
          >
            <div className="flex items-start gap-2 md:gap-3">
              <div className="w-7 h-7 md:w-8 md:h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <DollarSign className="text-blue-500" size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-white font-semibold text-xs md:text-sm mb-0.5 md:mb-1 truncate">Review Interest Rate Exposure</h4>
                <p className="text-gray-400 text-[10px] md:text-xs mb-1 md:mb-2 line-clamp-2">High probability risk identified. Consider fixed-rate locks.</p>
                <span className="text-blue-400 text-[10px] md:text-xs font-semibold">PRIORITY</span>
              </div>
            </div>
          </div>

          {/* Action 5 */}
          <div 
            className="bg-black/50 border border-gray-400/30 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-black/70 transition-colors"
            onClick={() => openModal('action', {
              title: 'Assess Design District Performance',
              description: '20% ROI below portfolio average. Improvement plan needed.',
              priority: 'REVIEW',
              details: 'Design District Warehouse underperforming with 20% ROI vs 25% portfolio average. Occupancy at 90% with high tenant turnover.',
              recommendations: [
                'Conduct market analysis for pricing adjustment',
                'Implement tenant retention program',
                'Invest in property improvements',
                'Evaluate property repositioning strategy'
              ]
            })}
          >
            <div className="flex items-start gap-2 md:gap-3">
              <div className="w-7 h-7 md:w-8 md:h-8 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Building2 className="text-purple-500" size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-white font-semibold text-xs md:text-sm mb-0.5 md:mb-1 truncate">Assess Design District Performance</h4>
                <p className="text-gray-400 text-[10px] md:text-xs mb-1 md:mb-2 line-clamp-2">20% ROI below portfolio average. Improvement plan needed.</p>
                <span className="text-purple-400 text-[10px] md:text-xs font-semibold">REVIEW</span>
              </div>
            </div>
          </div>

          {/* Action 6 */}
          <div 
            className="bg-black/50 border border-gray-400/30 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-black/70 transition-colors"
            onClick={() => openModal('action', {
              title: 'Expand Preventive Maintenance',
              description: 'Medium risk mitigation strategy showing positive results.',
              priority: 'SUGGESTED',
              details: 'Current preventive maintenance program reducing emergency repairs by 35% and extending asset life. Expansion recommended.',
              recommendations: [
                'Increase maintenance budget by 15%',
                'Implement IoT monitoring systems',
                'Expand program to additional properties',
                'Train staff on predictive maintenance'
              ]
            })}
          >
            <div className="flex items-start gap-2 md:gap-3">
              <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="text-teal-500" size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-white font-semibold text-xs md:text-sm mb-0.5 md:mb-1 truncate">Expand Preventive Maintenance</h4>
                <p className="text-gray-400 text-[10px] md:text-xs mb-1 md:mb-2 line-clamp-2">Medium risk mitigation strategy showing positive results.</p>
                <span className="text-teal-400 text-[10px] md:text-xs font-semibold">SUGGESTED</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Component */}
      {selectedModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 md:p-4" onClick={closeModal}>
          <div className="bg-black border-2 border-gray-400 rounded-xl p-4 md:p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-lg md:text-2xl font-bold truncate pr-2">
                {selectedModal === 'kpi' && modalData?.title}
                {selectedModal === 'property' && modalData?.property}
                {selectedModal === 'risk' && `Risk: ${modalData?.project}`}
                {selectedModal === 'detailedRisk' && `Risk: ${modalData?.category}`}
                {selectedModal === 'action' && modalData?.title}
              </h2>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-white w-8 h-8 flex items-center justify-center flex-shrink-0"
              >
                <X size={20} className="md:hidden" />
                <X size={24} className="hidden md:block" />
              </button>
            </div>

            {/* KPI Modal Content */}
            {selectedModal === 'kpi' && (
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="text-2xl md:text-4xl font-bold">{modalData.value}</div>
                  <div className="text-green-500 text-base md:text-xl font-semibold">{modalData.change}</div>
                </div>
                
                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <p className="text-gray-300 text-xs md:text-base">{modalData.details}</p>
                </div>

                <div>
                  <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Breakdown</h3>
                  <div className="space-y-2 md:space-y-3">
                    {modalData.breakdown.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2.5 md:p-3 bg-white/5 border border-gray-400/20 rounded-lg">
                        <div className="min-w-0 flex-1">
                          <div className="text-white font-semibold text-xs md:text-base truncate">{item.category}</div>
                          <div className="text-gray-400 text-[10px] md:text-sm">{item.percentage} of total</div>
                        </div>
                        <div className="text-base md:text-xl font-bold text-teal-500 ml-2 flex-shrink-0">{item.amount}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Property Modal Content */}
            {selectedModal === 'property' && (
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-3 gap-2 md:gap-4">
                  <div className="p-2.5 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Revenue</div>
                    <div className="text-lg md:text-2xl font-bold text-white">${modalData.revenue}M</div>
                  </div>
                  <div className="p-2.5 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Expenses</div>
                    <div className="text-lg md:text-2xl font-bold text-white">${modalData.expenses}M</div>
                  </div>
                  <div className="p-2.5 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Profit</div>
                    <div className="text-lg md:text-2xl font-bold text-green-500">${modalData.profit}M</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 md:gap-4">
                  <div className="p-2.5 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">ROI</div>
                    <div className="text-lg md:text-2xl font-bold text-teal-500">{modalData.roi}</div>
                  </div>
                  <div className="p-2.5 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Occupancy</div>
                    <div className="text-lg md:text-2xl font-bold text-white">{modalData.occupancy}</div>
                  </div>
                  <div className="p-2.5 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-0.5 md:mb-1">Units</div>
                    <div className="text-lg md:text-2xl font-bold text-white">{modalData.units}</div>
                  </div>
                </div>

                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Average Rent per Unit</div>
                  <div className="text-2xl md:text-3xl font-bold text-purple-500">${modalData.avgRent}/month</div>
                </div>

                <div className="p-3 md:p-4 bg-gradient-to-r from-green-900/30 to-green-800/30 border border-green-500/50 rounded-lg">
                  <div className="text-green-400 font-semibold mb-1 md:mb-2 text-xs md:text-base">Performance Summary</div>
                  <p className="text-gray-300 text-[10px] md:text-sm">
                    {modalData.property} is generating ${modalData.revenue}M in annual revenue with an impressive ROI of {modalData.roi}. 
                    Current occupancy at {modalData.occupancy} across {modalData.units} units at an average rent of ${modalData.avgRent}/month.
                  </p>
                </div>
              </div>
            )}

            {/* Risk Modal Content */}
            {selectedModal === 'risk' && (
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Risk Level</div>
                    <div className={`inline-block px-3 md:px-4 py-1.5 md:py-2 rounded text-sm md:text-lg font-bold ${getRiskColor(modalData.risk)}`}>
                      {modalData.risk}
                    </div>
                  </div>
                  <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Probability</div>
                    <div className={`inline-block px-3 md:px-4 py-1.5 md:py-2 rounded text-sm md:text-lg font-bold ${getRiskColor(modalData.probability)}`}>
                      {modalData.probability}
                    </div>
                  </div>
                </div>

                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Contingency Fund</div>
                  <div className="text-2xl md:text-3xl font-bold text-yellow-500">{modalData.contingency}</div>
                </div>

                <div className="p-3 md:p-4 bg-red-900/30 border border-red-500/50 rounded-lg">
                  <div className="text-red-400 font-semibold mb-1 md:mb-2 text-xs md:text-base">Risk Assessment</div>
                  <p className="text-gray-300 text-[10px] md:text-sm">
                    {modalData.project} has been identified as a {modalData.risk.toLowerCase()} risk project with {modalData.probability.toLowerCase()} probability. 
                    A contingency fund of {modalData.contingency} has been allocated to mitigate potential issues. Regular monitoring and proactive management required.
                  </p>
                </div>
              </div>
            )}

            {/* Detailed Risk Modal Content */}
            {selectedModal === 'detailedRisk' && (
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Impact Level</div>
                    <div className={`inline-block px-3 md:px-4 py-1.5 md:py-2 rounded text-sm md:text-lg font-bold ${getRiskColor(modalData.impact)}`}>
                      {modalData.impact}
                    </div>
                  </div>
                  <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                    <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Probability</div>
                    <div className={`inline-block px-3 md:px-4 py-1.5 md:py-2 rounded text-sm md:text-lg font-bold ${getRiskColor(modalData.probability)}`}>
                      {modalData.probability}
                    </div>
                  </div>
                </div>

                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Mitigation Strategy</div>
                  <div className="text-base md:text-xl font-semibold text-teal-500">{modalData.mitigation}</div>
                </div>

                <div className="p-3 md:p-4 bg-purple-900/30 border border-purple-500/50 rounded-lg">
                  <div className="text-purple-400 font-semibold mb-1 md:mb-2 text-xs md:text-base">Risk Analysis</div>
                  <p className="text-gray-300 text-[10px] md:text-sm">
                    {modalData.category} poses a {modalData.impact.toLowerCase()} impact risk with {modalData.probability.toLowerCase()} probability of occurrence. 
                    The portfolio strategy includes {modalData.mitigation.toLowerCase()} to minimize exposure and protect asset values.
                  </p>
                </div>
              </div>
            )}

            {/* Action Modal Content */}
            {selectedModal === 'action' && (
              <div className="space-y-4 md:space-y-6">
                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <div className="text-gray-400 text-[10px] md:text-sm mb-1 md:mb-2">Priority Level</div>
                  <div className="text-xl md:text-2xl font-bold text-yellow-500">{modalData.priority}</div>
                </div>

                <div className="p-3 md:p-4 bg-white/5 border border-gray-400/20 rounded-lg">
                  <p className="text-gray-300 text-xs md:text-base">{modalData.description}</p>
                </div>

                <div className="p-3 md:p-4 bg-blue-900/30 border border-blue-500/50 rounded-lg">
                  <div className="text-blue-400 font-semibold mb-1 md:mb-2 text-xs md:text-base">Situation Analysis</div>
                  <p className="text-gray-300 text-[10px] md:text-sm">{modalData.details}</p>
                </div>

                <div>
                  <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Recommended Actions</h3>
                  <div className="space-y-2 md:space-y-3">
                    {modalData.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-2 md:gap-3 p-2.5 md:p-3 bg-white/5 border border-gray-400/20 rounded-lg">
                        <div className="w-5 h-5 md:w-6 md:h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="text-teal-500" size={12} />
                        </div>
                        <p className="text-gray-300 text-[10px] md:text-sm">{rec}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateManagingPartnerReports;